import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import type { OrgMember } from '../../../hooks/useOrgMembers';
import { organizationStructureService } from '../../../services/admin/organizationStructureService';
import { getRoles } from '../../../services/organizationStructureService';
import { LST_CARGO_LABELS, mapRoleTypeToCargo, type LstCargo } from '../types';

export interface LstUserContext {
  lstCargo: LstCargo;
  /** Nome do cargo na organização (workflow_roles_v9) ou camada LST. */
  roleName: string;
  /** Nome do setor (org_departments) ou null se não atribuído. */
  departmentName: string | null;
  loading: boolean;
}

/**
 * Resolve cargo e setor reais do usuário logado a partir da hierarquia Supabase
 * (user_organizations + workflow_roles_v9 + org_departments).
 */
export function useLstUserContext(
  organizationId: string | undefined,
  member: OrgMember | null,
  enabled = true,
): LstUserContext {
  const { user } = useAuth();
  const orgReady = !!organizationId && enabled;

  const { data: departments = [], isLoading: deptLoading } = useQuery({
    queryKey: ['lst', 'departments', organizationId],
    queryFn: () => organizationStructureService.fetchDepartments(organizationId!),
    enabled: orgReady,
    staleTime: 300_000,
  });

  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['lst', 'roles', organizationId],
    queryFn: () => getRoles(organizationId!),
    enabled: orgReady,
    staleTime: 300_000,
  });

  return useMemo(() => {
    const lstCargo = mapRoleTypeToCargo(member?.roleType ?? null);
    const role = member?.roleId ? roles.find((r) => r.id === member.roleId) : undefined;
    const meta = user as { user_metadata?: Record<string, unknown> } | null;
    const metaCargo =
      typeof meta?.user_metadata?.cargo === 'string' ? meta.user_metadata.cargo.trim() : '';
    const metaSetor =
      typeof meta?.user_metadata?.setor === 'string'
        ? meta.user_metadata.setor.trim()
        : typeof meta?.user_metadata?.department === 'string'
          ? meta.user_metadata.department.trim()
          : '';

    const roleName = role?.name?.trim() || metaCargo || LST_CARGO_LABELS[lstCargo];
    const departmentName =
      member?.departmentId != null
        ? departments.find((d) => d.id === member.departmentId)?.name?.trim() ?? null
        : metaSetor || null;

    return {
      lstCargo,
      roleName,
      departmentName,
      loading: deptLoading || rolesLoading,
    };
  }, [member, departments, roles, deptLoading, rolesLoading, user]);
}
